import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Table, Form, InputGroup, Button, Row, Col, Pagination, Badge, Modal, Spinner, Alert, Card } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { showToast } from '../../../../utils/toast';
import distributorService from '../../../../services/DistributorService';

const DistributorVPAWhitelist = () => {
  const [vpaList, setVpaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedVPA, setSelectedVPA] = useState(null);
  const [statsData, setStatsData] = useState({
    total_entries: 0,
    verified_count: 0,
    pending_count: 0,
    rejected_count: 0
  });
  
  const [formData, setFormData] = useState({
    name: '',
    vpa: '',
    user: '',
    status: 'Pending'
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [retailers, setRetailers] = useState([]);
  const [retailerSearchQuery, setRetailerSearchQuery] = useState('');
  const [retailerSearchLoading, setRetailerSearchLoading] = useState(false);

  // Debounce timer reference
  const [searchTimeoutId, setSearchTimeoutId] = useState(null);
  
  // Ref to prevent duplicate calls in StrictMode
  const hasInitialized = useRef(false);

  // Fetch VPA statistics
  const fetchVPAStats = async () => {
    try {
      const response = await distributorService.getVPAStats();
      if (response.success) {
        setStatsData(response.data);
      }
    } catch (error) {
      console.error('Error fetching VPA stats:', error);
    }
  };

  // Search retailers for dropdown
  const searchRetailers = async (searchTerm = '') => {
    setRetailerSearchLoading(true);
    try {
      const response = await distributorService.getRetailerProfiles({
        search: searchTerm,
        page: 1,
        limit: 20
      });
      
      if (response.success) {
        setRetailers(response.data.results || []);
      }
    } catch (error) {
      console.error('Error searching retailers:', error);
    } finally {
      setRetailerSearchLoading(false);
    }
  };

  // Fetch VPA whitelist entries
  const fetchVPAEntries = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: rowsPerPage
      };
      
      if (searchQuery) params.search = searchQuery;
      if (statusFilter) params.status = statusFilter;
      
      const response = await distributorService.getVPAEntries(params);
      
      if (response.success) {
        setVpaList(response.data.results || []);
        setTotalCount(response.data.count || 0);
        
        // If current page exceeds total pages after data load, reset to page 1
        const newTotalPages = Math.ceil((response.data.count || 0) / rowsPerPage);
        if (currentPage > newTotalPages && newTotalPages > 0) {
          setCurrentPage(1);
        }
      }
    } catch (error) {
      console.error('Error fetching VPA entries:', error);
      showToast.error('Failed to fetch VPA entries');
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch function
  const debouncedFetchEntries = useCallback(() => {
    if (searchTimeoutId) {
      clearTimeout(searchTimeoutId);
    }
    
    const timeoutId = setTimeout(() => {
      fetchVPAEntries();
    }, 300); // 300ms debounce
    
    setSearchTimeoutId(timeoutId);
  }, [currentPage, rowsPerPage, searchQuery, statusFilter]);

  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchVPAStats();
    }
  }, []);

  useEffect(() => {
    if (searchQuery || statusFilter) {
      debouncedFetchEntries();
    } else {
      fetchVPAEntries();
    }
  }, [currentPage, rowsPerPage, searchQuery, statusFilter]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutId) {
        clearTimeout(searchTimeoutId);
      }
    };
  }, [searchTimeoutId]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  
  const handleSearch = (query) => {
    // React 18 automatic batching will batch these state updates
    setSearchQuery(query);
    setCurrentPage(1);
  };
  
  const handleFilterChange = (filterType, value) => {
    // React 18 automatic batching will batch these state updates
    if (filterType === 'status') setStatusFilter(value);
    setCurrentPage(1);
  };


  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(vpaList);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'VPA Whitelist');
    XLSX.writeFile(workbook, `vpa_whitelist_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // VPA validation function
  const validateVPA = (vpa) => {
    const vpaRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+$/;
    return vpaRegex.test(vpa);
  };

  const handleAdd = () => {
    setSelectedVPA(null);
    setModalMode('add');
    setFormData({
      name: '',
      vpa: '',
      user: '',
      status: 'Pending'
    });
    setFormErrors({});
    setRetailerSearchQuery('');
    setRetailers([]);
    setShowModal(true);
    // Load initial retailers
    searchRetailers();
  };

  const handleEdit = (vpa) => {
    setSelectedVPA(vpa);
    setModalMode('edit');
    setFormData({
      name: vpa.name,
      vpa: vpa.vpa || '',
      user: vpa.user || '',
      status: vpa.status
    });
    setFormErrors({});
    setRetailerSearchQuery('');
    setRetailers([]);
    setShowModal(true);
    // Load retailers if editing
    searchRetailers();
  };

  const handleDelete = async (vpa) => {
    if (window.confirm(`Are you sure you want to delete ${vpa.name}?`)) {
      try {
        const response = await distributorService.deleteVPAEntry(vpa.id);
        if (response.success) {
          showToast.success(`${vpa.name} deleted successfully`);
          fetchVPAEntries();
          fetchVPAStats();
        } else {
          showToast.error(response.error || 'Failed to delete VPA entry');
        }
      } catch (error) {
        console.error('Error deleting VPA entry:', error);
        showToast.error('Failed to delete VPA entry');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormErrors({});
    
    // Validation
    const errors = {};
    if (!formData.name?.trim()) errors.name = 'Name is required';
    if (!formData.vpa?.trim()) {
      errors.vpa = 'VPA is required';
    } else if (!validateVPA(formData.vpa)) {
      errors.vpa = 'Invalid VPA format (e.g., user@paytm)';
    }
    if (!formData.user) errors.user = 'Please select a retailer';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSaving(false);
      return;
    }
    
    try {
      let response;
      if (modalMode === 'add') {
        response = await distributorService.createVPAEntry(formData);
      } else {
        response = await distributorService.updateVPAEntry(selectedVPA.id, formData);
      }
      
      if (response && response.success) {
        showToast.success(response.message || `VPA entry ${modalMode === 'add' ? 'created' : 'updated'} successfully`);
        setShowModal(false);
        fetchVPAEntries();
        fetchVPAStats();
      } else {
        showToast.error(response?.error || `Failed to ${modalMode === 'add' ? 'create' : 'update'} VPA entry`);
      }
    } catch (error) {
      console.error('Error saving VPA entry:', error);
      
      if (error.response?.data?.errors) {
        // Handle field-specific validation errors
        setFormErrors(error.response.data.errors);
        
        // Also show the general validation message if available
        if (error.response.data.message) {
          showToast.error(error.response.data.message);
        }
      } else if (error.response?.data?.message) {
        showToast.error(error.response.data.message);
      } else {
        showToast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} VPA entry`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'Verified':
        return <Badge bg="success">Verified</Badge>;
      case 'Rejected':
        return <Badge bg="danger">Rejected</Badge>;
      case 'Pending':
      default:
        return <Badge bg="warning">Pending</Badge>;
    }
  };

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-shield-alt me-2"></i>
          VPA Whitelist
        </h5>
        <div className="d-flex gap-2">
          <Button variant="success" onClick={exportToExcel}>
            <i className="fas fa-file-excel me-2"></i>
            Export Excel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            <i className="fas fa-plus me-2"></i>
            Add VPA Manually
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col md={3} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div className="icon-circle bg-primary text-white me-3" style={{ width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-list"></i>
                </div>
                <div>
                  <h3 className="mb-0 text-primary">{statsData.total_entries}</h3>
                  <small className="text-muted">Total Entries</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div className="icon-circle bg-success text-white me-3" style={{ width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-check-circle"></i>
                </div>
                <div>
                  <h3 className="mb-0 text-success">{statsData.verified_count}</h3>
                  <small className="text-muted">Verified</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div className="icon-circle bg-warning text-white me-3" style={{ width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-clock"></i>
                </div>
                <div>
                  <h3 className="mb-0 text-warning">{statsData.pending_count}</h3>
                  <small className="text-muted">Pending</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={3} className="mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="text-center">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <div className="icon-circle bg-danger text-white me-3" style={{ width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-times-circle"></i>
                </div>
                <div>
                  <h3 className="mb-0 text-danger">{statsData.rejected_count}</h3>
                  <small className="text-muted">Rejected</small>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-4">
        <Col md={3}>
          <InputGroup>
            <InputGroup.Text>
              <i className="fas fa-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search VPA entries..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={2}>
          <Form.Select
            value={statusFilter}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Verified">Verified</option>
            <option value="Rejected">Rejected</option>
          </Form.Select>
        </Col>
        <Col md={7}>
          {/* Space for future filters */}
        </Col>
      </Row>

      {/* Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Name</th>
                <th>VPA Address</th>
                <th>Status</th>
                <th>User Mobile</th>
                <th>User Name</th>
                <th>Shop Name</th>
                <th>Customer ID</th>
                <th>Distributor Name</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {vpaList.map((vpa) => (
                <tr key={vpa.id}>
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(vpa)}
                      >
                        <i className="fas fa-edit"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(vpa)}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </td>
                  <td><strong>{vpa.name}</strong></td>
                  <td>
                    <code className="text-primary">{vpa.vpa}</code>
                  </td>
                  <td>{getStatusBadge(vpa.status)}</td>
                  <td>
                    <div>
                      <div>{vpa.user_mobile}</div>
                      <small className="text-muted">ID: {vpa.id}</small>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{vpa.user_name}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="fw-bold text-info">{vpa.user_shop_name || 'N/A'}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div className="fw-bold">{vpa.user_customer_id || 'N/A'}</div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div>{vpa.distributor_name}</div>
                    </div>
                  </td>
                  <td>
                    <small className="text-muted">
                      {new Date(vpa.created_at).toLocaleDateString()}
                    </small>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {vpaList.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No VPA entries found</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          {totalCount > 0 ? (
            `Showing ${((currentPage - 1) * rowsPerPage) + 1} to ${Math.min(currentPage * rowsPerPage, totalCount)} of ${totalCount} entries`
          ) : (
            'No entries found'
          )}
        </div>
        {totalPages > 0 && (
          <Pagination>
            <Pagination.First 
              onClick={() => setCurrentPage(1)} 
              disabled={currentPage === 1} 
            />
            <Pagination.Prev 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
              disabled={currentPage === 1} 
            />
          {totalPages > 0 && [...Array(Math.min(5, totalPages))].map((_, index) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = index + 1;
            } else if (currentPage <= 3) {
              pageNum = index + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + index;
            } else {
              pageNum = currentPage - 2 + index;
            }
            
            if (pageNum > 0 && pageNum <= totalPages) {
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === currentPage}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            }
            return null;
          })}
            <Pagination.Next 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
              disabled={currentPage === totalPages} 
            />
            <Pagination.Last 
              onClick={() => setCurrentPage(totalPages)} 
              disabled={currentPage === totalPages} 
            />
          </Pagination>
        )}
      </div>

      {/* Add/Edit VPA Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === 'add' ? 'Add New' : 'Edit'} VPA Entry</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {Object.keys(formErrors).length > 0 && (
              <Alert variant="danger">
                <ul className="mb-0">
                  {Object.entries(formErrors).map(([field, errors]) => (
                    <li key={field}>{field}: {Array.isArray(errors) ? errors.join(', ') : errors}</li>
                  ))}
                </ul>
              </Alert>
            )}
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter VPA holder name"
                    isInvalid={formErrors.name}
                    required
                  />
                  {formErrors.name && (
                    <Form.Control.Feedback type="invalid">
                      {Array.isArray(formErrors.name) ? formErrors.name.join(', ') : formErrors.name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>VPA Address *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.vpa}
                    onChange={(e) => handleInputChange('vpa', e.target.value)}
                    placeholder="Enter VPA (e.g., john.doe@paytm)"
                    isInvalid={formErrors.vpa}
                    required
                  />
                  {formErrors.vpa && (
                    <Form.Control.Feedback type="invalid">
                      {Array.isArray(formErrors.vpa) ? formErrors.vpa.join(', ') : formErrors.vpa}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Select Retailer *</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      placeholder="Search retailers..."
                      value={retailerSearchQuery}
                      onChange={(e) => {
                        setRetailerSearchQuery(e.target.value);
                        searchRetailers(e.target.value);
                      }}
                      isInvalid={formErrors.user}
                    />
                    {retailerSearchLoading && (
                      <div className="position-absolute top-50 end-0 translate-middle-y me-2">
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                  </div>
                  
                  {retailers.length > 0 && (
                    <div className="mt-2" style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.375rem' }}>
                      {retailers.map((retailer) => (
                        <div
                          key={retailer.id}
                          className={`p-2 border-bottom cursor-pointer ${formData.user === retailer.id ? 'bg-primary text-white' : 'hover-bg-light'}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            handleInputChange('user', retailer.retailer_id);
                            setRetailerSearchQuery(`${retailer.user_name} (${retailer.user_mobile})`);
                            setRetailers([]);
                          }}
                        >
                          <div className="fw-bold">{retailer.user_name}</div>
                          <small className="text-muted">{retailer.user_mobile} - {retailer.user_customer_id}</small>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {formErrors.user && (
                    <div className="text-danger small mt-1">
                      {Array.isArray(formErrors.user) ? formErrors.user.join(', ') : formErrors.user}
                    </div>
                  )}
                </Form.Group>
              </Col>
              
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Status *</Form.Label>
                  <Form.Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    required
                  >
                    <option value="Pending">Pending</option>
                    <option value="Verified">Verified</option>
                    <option value="Rejected">Rejected</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {modalMode === 'add' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                <>
                  {modalMode === 'add' ? 'Create' : 'Update'} VPA Entry
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default DistributorVPAWhitelist;